import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  DAYS_OF_WEEK,
  FHIR_MIRROR_ENTITIES,
  PRACTITIONER_ROLE_CODES,
  REVERSE_SYNC_RESOURCES,
} from '@eluve/fhir-utils';

const c = initContract();

const medplumDisabledResponseSchema = z.object({
  enabled: z.literal(false),
});

/**
 * Summary of the AccessPolicy the server attached to the caller's Medplum
 * ProjectMembership during this setup call. Rendered on the FHIR Profile
 * tab so the user can see what access they actually have — the truth comes
 * from this response, not from client-side role inference.
 */
const medplumAccessSummarySchema = z.object({
  /** 'broad' = no AccessPolicy attached; 'scoped-gp' = Patient?general-practitioner=%profile. */
  level: z.enum(['broad', 'scoped-gp']),
  /** Which role branch drove the decision. */
  reason: z.enum(['eluve-admin', 'tenant-admin', 'tenant-member']),
});

const medplumSetupResponseSchema = z.object({
  clientId: z.string(),
  projectId: z.string(),
  practitionerId: z.string().nullable(),
  access: medplumAccessSummarySchema.nullable(),
});

const medplumEnableResponseSchema = z.object({
  projectId: z.string(),
  clientId: z.string(),
  status: z.enum(['enabled', 'already_enabled']),
});

const medplumConfigResponseSchema = z.discriminatedUnion('enabled', [
  z.object({
    enabled: z.literal(true),
    projectId: z.string(),
    clientId: z.string(),
  }),
  medplumDisabledResponseSchema,
]);

const medplumCreateUserResponseSchema = z.union([
  z.object({
    status: z.literal('created'),
    practitionerId: z.string(),
    membershipId: z.string(),
  }),
  z.object({
    status: z.literal('already_exists'),
    practitionerId: z.string(),
  }),
]);

/**
 * `already_exists` = patients.medplum_resource_id was already filled, so no
 * Medplum round-trip happened. `created` = the identifier-keyed upsert ran —
 * it may have matched an existing FHIR Patient by identifier, but from the
 * caller's perspective the resource is now guaranteed to exist either way.
 */
const medplumEnsurePatientResponseSchema = z.object({
  fhirPatientId: z.string(),
  status: z.enum(['created', 'already_exists']),
});

const medplumCreateProjectResponseSchema = z.object({
  projectId: z.string(),
  clientId: z.string(),
  status: z.enum(['created', 'already_exists']),
});

const medplumCreateUserErrorResponseSchema = z.object({
  error: z.string(),
});

/** A recurring weekly window, wall-clock "HH:mm:ss" in the clinic timezone. */
const availabilityWindowSchema = z.object({
  daysOfWeek: z.array(z.enum(DAYS_OF_WEEK)).min(1),
  start: z.string().regex(/^\d{2}:\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}:\d{2}$/),
});

/**
 * A dated block on a clinician's calendar (`PractitionerRole.notAvailable`),
 * projected to `busy-unavailable` Slots so availability search skips it.
 * `start`/`end` are FHIR date ("2026-08-03") or dateTime; a date-only `end` is
 * INCLUSIVE of that whole day in the clinic's timezone.
 */
const timeOffEntrySchema = z.object({
  description: z.string().min(1),
  start: z.string().min(1),
  end: z.string().min(1),
});

/**
 * Employment/effective window for a member's hours at a location — FHIR
 * `date` values, either side open-ended. Feeds `PractitionerRole.period`,
 * which the booking engine clamps slot search to and rejects holds outside.
 */
const effectivePeriodSchema = z.object({
  start: z.string().min(1).optional(),
  end: z.string().min(1).optional(),
});

/**
 * What happened to a derived Schedule: `materialized` (bookable), or `removed`
 * with why (`role_inactive` / `no_services` / `no_hours` / `no_timezone`) —
 * authoring empty hours is a valid way to take a clinician off the books.
 * `removed` means the Schedule was deactivated, not deleted; its id is stable
 * across the cycle. `reason` stays an open string so new reasons don't break
 * old clients — they fall back to generic copy.
 */
const scheduleMaterializationSchema = z.object({
  status: z.enum(['materialized', 'removed', 'not_found']),
  scheduleId: z.string().optional(),
  reason: z.string().optional(),
});

const clinicianSchedulingSchema = z.object({
  practitionerRoleId: z.string().nullable(),
  healthcareServiceIds: z.array(z.string()),
  availability: z.array(availabilityWindowSchema),
  /**
   * Optional so a new client parsing an OLD server's response (rolling deploy)
   * still validates — read it as "no time off known" and fall back to `[]`.
   * Entries authored without a complete period are omitted: they block nothing.
   */
  timeOff: z.array(timeOffEntrySchema).optional(),
  /** Optional for rolling-deploy skew — absent means "none authored". */
  effective: effectivePeriodSchema.optional(),
});

const medplumPatientStatusResponseSchema = z.object({
  /** FHIR Patient versionId after the write (unchanged on a no-op). */
  versionId: z.string(),
});

/**
 * A JSONPatch operation as `diffResourceOps` emits it: top-level-field
 * granularity, so `path` is always `/<topLevelKey>` (plus `/meta/versionId` on
 * the leading `test`). The endpoint routes each op by that path and rejects
 * anything it does not recognise, so this schema deliberately validates shape
 * only.
 */
const fhirPatchOperationSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('add'), path: z.string(), value: z.unknown() }),
  z.object({ op: z.literal('replace'), path: z.string(), value: z.unknown() }),
  z.object({ op: z.literal('test'), path: z.string(), value: z.unknown() }),
  z.object({ op: z.literal('remove'), path: z.string() }),
]);

export type FhirPatchOperation = z.infer<typeof fhirPatchOperationSchema>;

const medplumPatientDemographicsResponseSchema = z.object({
  /**
   * FHIR Patient versionId after the FHIR half of the save. The inline Hasura
   * projection that follows can advance it again, so treat this as "the version
   * the write produced", not "the current version" — clients refetch anyway.
   */
  versionId: z.string(),
});

const medplumProjectSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  tenantId: z.string().nullable(),
});

const medplumDiagnosticsResponseSchema = z.object({
  healthy: z.boolean(),
  projects: z.array(medplumProjectSummarySchema),
  serverVersion: z.string().nullable(),
  error: z.string().nullable(),
});

export const medplumAdminContract = c.router(
  {
    diagnostics: {
      method: 'GET',
      path: '/diagnostics',
      responses: {
        200: medplumDiagnosticsResponseSchema,
      },
      summary: 'Get Medplum diagnostics: health check and list all projects',
    },
    backfillMirror: {
      method: 'POST',
      path: '/mirror/backfill/:tenantId',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z.object({
        entity: z.enum(FHIR_MIRROR_ENTITIES),
      }),
      responses: {
        200: z.object({ enqueued: z.number() }),
      },
      summary:
        'Enqueue a FHIR mirror re-sync for every row of an entity in a tenant',
    },
    backfillReverseSync: {
      method: 'POST',
      path: '/mirror/reverse-backfill/:tenantId',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z.object({
        resource: z.enum(REVERSE_SYNC_RESOURCES),
        /**
         * Re-drive one resource. Omit to re-drive every resource of this type
         * in the tenant's project — the blunt form, which on a project seeded
         * with synthetic patients creates an eluve row for each of them.
         */
        fhirId: z.string().uuid().optional(),
      }),
      responses: {
        200: z.object({ enqueued: z.number() }),
      },
      summary:
        'Re-drive the FHIR→eluve sync for one resource, or for a whole tenant',
    },
    ensureProjectFeatures: {
      method: 'POST',
      path: '/projects/:tenantId/features',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z.object({}),
      responses: {
        200: z.object({
          projectId: z.string(),
          /** Empty when the project was already current — the common case. */
          added: z.array(z.string()),
        }),
        404: medplumCreateUserErrorResponseSchema,
      },
      summary:
        "Enable any Medplum project features this tenant's project is missing",
    },
    syncAccessPolicies: {
      method: 'POST',
      path: '/policies/sync/:tenantId',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z.object({}),
      responses: {
        200: z.object({ synced: z.number(), skipped: z.number() }),
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        "Re-sync every tenant member's Medplum access policy to match their current role",
    },
  },
  { pathPrefix: '/medplum' },
);

export const medplumContract = c.router(
  {
    setup: {
      method: 'POST',
      path: '/setup',
      body: z.object({}),
      responses: {
        200: z.union([
          medplumDisabledResponseSchema,
          medplumSetupResponseSchema,
        ]),
      },
      summary: 'Setup Medplum project and user for the current tenant',
    },
    createProject: {
      method: 'POST',
      path: '/project',
      body: z.object({}),
      responses: {
        200: medplumCreateProjectResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary: 'Create Medplum project for the tenant',
    },
    enable: {
      method: 'POST',
      path: '/enable',
      body: z.object({}),
      responses: {
        200: z.union([
          medplumDisabledResponseSchema,
          medplumEnableResponseSchema,
        ]),
      },
      summary: 'Enable Medplum for tenant (admin only)',
    },
    getConfig: {
      method: 'GET',
      path: '/config',
      responses: {
        200: medplumConfigResponseSchema,
      },
      summary: 'Get Medplum configuration for tenant',
    },
    ensurePatient: {
      method: 'POST',
      path: '/patients/:patientId/ensure',
      body: z.object({}),
      pathParams: z.object({
        tenantId: z.string(),
        patientId: z.string(),
      }),
      responses: {
        200: medplumEnsurePatientResponseSchema,
        404: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        'Idempotently ensure a FHIR Patient exists for the Eluve patient',
    },
    setPatientStatus: {
      method: 'POST',
      path: '/patients/:patientId/status',
      pathParams: z.object({
        tenantId: z.string(),
        patientId: z.string(),
      }),
      body: z.object({
        status: z.enum(['deceased', 'left-practice']),
        set: z.boolean(),
        /** Optimistic lock: reject with 409 when the FHIR Patient has moved past this version. */
        expectedVersionId: z.string().optional(),
      }),
      responses: {
        200: medplumPatientStatusResponseSchema,
        404: medplumCreateUserErrorResponseSchema,
        409: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        'Set or clear a patient status flag (deceased / left practice) in FHIR and the Hasura mirror',
    },
    updatePatientDemographics: {
      method: 'POST',
      path: '/patients/:patientId/demographics',
      pathParams: z.object({
        tenantId: z.string(),
        patientId: z.string(),
      }),
      body: z.object({
        /**
         * The FHIR Patient the ops were diffed against. Verified to resolve to
         * the path's `patientId` before anything is applied — without that the
         * ops would let a caller patch any Patient in the tenant's project.
         */
        fhirId: z.string(),
        ops: z.array(fhirPatchOperationSchema).min(1),
        /**
         * Optimistic lock. Equivalent to the leading
         * `test /meta/versionId` op, which the endpoint accepts in `ops` and
         * consumes the same way; supplying both with different values is a 422.
         * Non-empty: an empty string would read as "no lock" and let the save
         * overwrite a concurrent edit.
         *
         * Optional in the schema but NOT optional to the endpoint: a request
         * resolving no version from either source is rejected 422, since an
         * unlocked save would silently overwrite a concurrent edit. Callers
         * forwarding `diffResourceOps` output satisfy this via the test op.
         */
        expectedVersionId: z.string().min(1).optional(),
      }),
      responses: {
        200: medplumPatientDemographicsResponseSchema,
        /** No such Eluve patient, or the posted FHIR id is not this patient's. */
        404: medplumCreateUserErrorResponseSchema,
        409: medplumCreateUserErrorResponseSchema,
        /** An op the endpoint cannot route, or Medplum is unavailable. */
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        'Apply a patient demographics edit, routing Hasura-owned fields to `patients` and the rest to FHIR in one atomic save',
    },
    setPractitionerRole: {
      method: 'POST',
      path: '/users/:userId/practitioner-role',
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string().uuid(),
      }),
      body: z.object({
        /** `null` unsets the role — deletes the eluve-owned PractitionerRole. */
        role: z.enum(PRACTITIONER_ROLE_CODES).nullable(),
        /** Omitted = the org-wide default role; set = the per-location override. */
        locationId: z.string().optional(),
      }),
      responses: {
        200: z.object({
          practitionerRoleId: z.string().nullable(),
          role: z.enum(PRACTITIONER_ROLE_CODES).nullable(),
          /**
           * The role write committed but re-deriving the member's access
           * entries afterwards failed, so their permissions still reflect the
           * OLD role until their next login or an admin re-sync. Optional so
           * older clients, which never read it, keep parsing this response.
           */
          accessSyncFailed: z.boolean().optional(),
        }),
        404: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        "Set a member's FHIR practice role (clinician / front desk), org-wide or per location",
    },
    getClinicianScheduling: {
      method: 'GET',
      path: '/users/:userId/scheduling',
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string().uuid(),
      }),
      query: z.object({
        // locations.id is text, not uuid (dev seeds use readable slugs)
        locationId: z.string().min(1),
      }),
      responses: {
        200: clinicianSchedulingSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        "A member's authored weekly hours and deliverable services at a location",
    },
    setClinicianScheduling: {
      method: 'POST',
      path: '/users/:userId/scheduling',
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string().uuid(),
      }),
      body: z.object({
        locationId: z.string().min(1),
        healthcareServiceIds: z.array(z.string()),
        availability: z.array(availabilityWindowSchema),
        /**
         * Omitted entirely = leave existing time off untouched, so older
         * clients that never send it don't wipe it; `[]` clears it.
         */
        timeOff: z.array(timeOffEntrySchema).optional(),
        /** Same skew contract: omitted leaves the window alone, `{}` clears it. */
        effective: effectivePeriodSchema.optional(),
      }),
      responses: {
        200: z.object({
          practitionerRoleId: z.string(),
          schedule: scheduleMaterializationSchema,
        }),
        404: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        "Author a member's weekly hours + services at a location and re-materialize their Schedule",
    },
    getClinicianTimeOff: {
      method: 'GET',
      path: '/users/:userId/time-off',
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          /** The union across the member's clinics, deduped, earliest first. */
          timeOff: z.array(timeOffEntrySchema),
        }),
        422: medplumCreateUserErrorResponseSchema,
      },
      summary: "A member's time off, across every clinic they work at",
    },
    setClinicianTimeOff: {
      method: 'PUT',
      path: '/users/:userId/time-off',
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string().uuid(),
      }),
      body: z.object({
        /** Replaces the member's time off everywhere; `[]` clears it. */
        timeOff: z.array(timeOffEntrySchema),
      }),
      responses: {
        200: z.object({
          /** One entry per clinic the member works at, all written alike. */
          roles: z.array(
            z.object({
              practitionerRoleId: z.string(),
              schedule: scheduleMaterializationSchema,
            }),
          ),
        }),
        404: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        "Author a member's time off across every clinic they work at and re-materialize their Schedules",
    },
    createUser: {
      method: 'POST',
      path: '/users/:userId/create',
      body: z.object({}),
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string(),
      }),
      responses: {
        200: medplumCreateUserResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary: 'Create a Medplum user for the specified user',
    },
  },
  { pathPrefix: '/:tenantId/medplum' },
);
